import type { ParsedAdEntity } from './parse-ad-entities-output.util';
import type {
  ParsedAdGraphEdge,
  ParsedAdGraphNode,
  ParsedAdGraphOutput,
} from './parse-ad-graph-output.util';

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function mapAdGraphNode(node: UnknownRecord): ParsedAdGraphNode | null {
  const id = readString(node.id) ?? readString(node.objectId);
  if (!id) {
    return null;
  }

  const kind = readString(node.kind) ?? readString(node.label) ?? 'Unknown';
  const label =
    readString(node.label) ??
    readString(node.displayName) ??
    readString(node.samAccountName) ??
    id;

  return {
    kind,
    label,
    id,
    isCrownJewel: node.isCrownJewel === true ? true : undefined,
    isOwned: node.isOwned === true ? true : undefined,
  };
}

function mapCypherNode(id: string, node: UnknownRecord): ParsedAdGraphNode {
  const properties = isRecord(node.properties) ? node.properties : {};
  const kind =
    readString(node.kind) ??
    readString(node.label) ??
    (Array.isArray(node.kinds) ? readString(node.kinds[0]) : undefined) ??
    'Unknown';
  const label =
    readString(properties.name) ??
    readString(properties.samaccountname) ??
    readString(node.displayName) ??
    readString(node.objectId) ??
    id;

  return { kind, label, id: readString(node.objectId) ?? id };
}

function mapAdGraphEdge(
  edge: UnknownRecord,
  nodeReferenceIds?: ReadonlyMap<string, string>,
): ParsedAdGraphEdge | null {
  const rawSourceId = readString(edge.sourceId) ?? readString(edge.source);
  const rawTargetId = readString(edge.targetId) ?? readString(edge.target);
  const relationshipType =
    readString(edge.relationshipType) ??
    readString(edge.kind) ??
    readString(edge.label);

  if (!rawSourceId || !rawTargetId || !relationshipType) {
    return null;
  }

  const sourceId = nodeReferenceIds?.get(rawSourceId) ?? rawSourceId;
  const targetId = nodeReferenceIds?.get(rawTargetId) ?? rawTargetId;

  return {
    sourceId,
    targetId,
    relationshipType,
    isAbusable: edge.isAbusable === true ? true : undefined,
  };
}

function mapEntitySummary(entity: UnknownRecord): ParsedAdEntity | null {
  const nodeId = readString(entity.nodeId);
  const displayName = readString(entity.displayName);
  const entityKind = readString(entity.entityKind);

  if (!nodeId || !displayName || !entityKind) {
    return null;
  }

  return {
    nodeId,
    displayName,
    entityKind,
    isCrownJewel: entity.isCrownJewel === true,
    samAccountName: readString(entity.samAccountName),
  };
}

export function parseMcpBloodhoundGraphOutput(
  parsed: unknown,
): ParsedAdGraphOutput | null {
  if (!isRecord(parsed)) {
    return null;
  }

  let nodes: ParsedAdGraphNode[] = [];
  let edges: ParsedAdGraphEdge[] = [];
  let cypherNodeReferences: Map<string, string> | undefined;

  if (Array.isArray(parsed.nodes)) {
    nodes = parsed.nodes
      .filter(isRecord)
      .map(mapAdGraphNode)
      .filter((node): node is ParsedAdGraphNode => node !== null);
  } else if (isRecord(parsed.nodes)) {
    cypherNodeReferences = new Map<string, string>();
    nodes = Object.entries(parsed.nodes)
      .filter((entry): entry is [string, UnknownRecord] => isRecord(entry[1]))
      .map(([mapKey, node]) => {
        const mapped = mapCypherNode(mapKey, node);
        cypherNodeReferences?.set(mapKey, mapped.id);
        cypherNodeReferences?.set(mapped.id, mapped.id);
        const objectId = readString(node.objectId);
        if (objectId) {
          cypherNodeReferences?.set(objectId, mapped.id);
        }
        return mapped;
      });
  }

  if (Array.isArray(parsed.edges)) {
    edges = parsed.edges
      .filter(isRecord)
      .map((edge) => mapAdGraphEdge(edge, cypherNodeReferences))
      .filter((edge): edge is ParsedAdGraphEdge => edge !== null);
  }

  if (nodes.length === 0 && edges.length === 0) {
    return null;
  }

  const truncated = parsed.truncated === true;
  const totalCount =
    typeof parsed.totalCount === 'number' ? parsed.totalCount : undefined;

  return {
    nodeCount: nodes.length,
    totalCount,
    truncated,
    nodes,
    edgeCount: edges.length,
    edges,
  };
}

export function parseMcpBloodhoundEntitiesOutput(
  parsed: unknown,
): ParsedAdEntity[] | null {
  if (!isRecord(parsed) || !Array.isArray(parsed.entities)) {
    return null;
  }

  const entities = parsed.entities
    .filter(isRecord)
    .map(mapEntitySummary)
    .filter((entity): entity is ParsedAdEntity => entity !== null);

  return entities.length > 0 ? entities : null;
}

export function parseMcpBloodhoundJsonOutput(
  output: string,
): ParsedAdGraphOutput | ParsedAdEntity[] | null {
  const trimmed = output.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(trimmed);
    return (
      parseMcpBloodhoundGraphOutput(parsed) ??
      parseMcpBloodhoundEntitiesOutput(parsed)
    );
  } catch {
    return null;
  }
}
